import { useEffect, useRef, type MouseEvent } from 'react';
import { RiMap2Line } from 'react-icons/ri';
import { useCanvasStore } from '../canvas/canvasStore';
import { getBoundWithRotation, resolveEndpoint, ropePoints } from '../canvas/core/geometry';

const WIDTH = 200;
const HEIGHT = 132;
const PAD = 36;

/** 鸟瞰图：绘制全部元素与当前视野，可点击跳转。 */
export function Minimap() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const controller = useCanvasStore(state => state.controller);
  const revision = useCanvasStore(state => state.revision);

  const computeLayout = () => {
    if (!controller) return null;
    const view = controller.viewport.viewBounds;
    const bounds = [...controller.surface.all().map(el => getBoundWithRotation(el)), view];
    if (bounds.length === 0) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const b of bounds) {
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.w);
      maxY = Math.max(maxY, b.y + b.h);
    }
    const scale = Math.min(
      (WIDTH - PAD) / Math.max(1, maxX - minX),
      (HEIGHT - PAD) / Math.max(1, maxY - minY)
    );
    const offsetX = (WIDTH - (maxX - minX) * scale) / 2 - minX * scale;
    const offsetY = (HEIGHT - (maxY - minY) * scale) / 2 - minY * scale;
    return { scale, offsetX, offsetY, view };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !controller) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = WIDTH * dpr;
    canvas.height = HEIGHT * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    const layout = computeLayout();
    if (!layout) return;
    const { scale, offsetX, offsetY } = layout;
    const toMini = (x: number, y: number): [number, number] => [x * scale + offsetX, y * scale + offsetY];

    for (const el of controller.surface.all()) {
      if (el.type === 'connector' || el.type === 'rope') {
        const props = el.props as {
          source?: { id?: string; point?: [number, number] };
          target?: { id?: string; point?: [number, number] };
          mode?: 'straight' | 'orth';
          sag?: number;
        };
        if (!props.source || !props.target) continue;
        const points =
          el.type === 'rope'
            ? ropePoints(
                resolveEndpoint(props.source, controller.surface.lookup),
                resolveEndpoint(props.target, controller.surface.lookup),
                props.sag ?? 0.12,
                12
              )
            : controller.connectorPoints(props.source, props.target, props.mode ?? 'straight');
        ctx.strokeStyle = el.type === 'rope' ? 'rgba(179,38,30,0.65)' : 'rgba(120,113,108,0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        const start = toMini(points[0][0], points[0][1]);
        const end = toMini(points[points.length - 1][0], points[points.length - 1][1]);
        ctx.moveTo(start[0], start[1]);
        ctx.lineTo(end[0], end[1]);
        ctx.stroke();
        continue;
      }
      const b = getBoundWithRotation(el);
      const p = toMini(b.x, b.y);
      const w = Math.max(2, b.w * scale);
      const h = Math.max(2, b.h * scale);
      ctx.fillStyle =
        el.refKind === 'target'
          ? '#1e3a5f'
          : el.refKind === 'entity'
            ? '#6d28d9'
            : el.refKind === 'hypothesis'
              ? '#a16207'
              : el.type === 'circle'
                ? 'rgba(179,38,30,0.5)'
                : el.type === 'highlight'
                  ? 'rgba(253,224,71,0.7)'
                  : el.type === 'tape'
                    ? 'rgba(220,205,160,0.8)'
                    : el.type === 'photo'
                      ? '#0f7a5a'
                      : el.type === 'pin'
                        ? '#b3261e'
                        : el.type === 'frame'
                          ? 'rgba(120,113,108,0.25)'
                          : 'rgba(120,113,108,0.6)';
      ctx.fillRect(p[0], p[1], w, h);
    }

    const { view } = layout;
    const vp = toMini(view.x, view.y);
    ctx.fillStyle = 'rgba(30,58,95,0.07)';
    ctx.fillRect(vp[0], vp[1], view.w * scale, view.h * scale);
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 1.25;
    ctx.strokeRect(vp[0], vp[1], view.w * scale, view.h * scale);
  }, [controller, revision]);

  const handleClick = (event: MouseEvent<HTMLCanvasElement>) => {
    if (!controller) return;
    const layout = computeLayout();
    if (!layout) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const miniX = ((event.clientX - rect.left) / rect.width) * WIDTH;
    const miniY = ((event.clientY - rect.top) / rect.height) * HEIGHT;
    controller.viewport.setCenter([
      (miniX - layout.offsetX) / layout.scale,
      (miniY - layout.offsetY) / layout.scale,
    ]);
    controller.requestRender();
    useCanvasStore.getState().bump();
  };

  return (
    <div className="pointer-events-auto absolute bottom-12 right-3 z-10 rounded-[var(--radius-lg)] border border-border bg-card/95 p-2 shadow-[0_10px_34px_rgba(27,28,30,0.14)] backdrop-blur">
      <div className="mb-1.5 flex items-center gap-1 text-[10px] text-muted-foreground">
        <RiMap2Line className="size-3" />
        鸟瞰
      </div>
      <canvas
        ref={canvasRef}
        width={WIDTH}
        height={HEIGHT}
        onClick={handleClick}
        className="block cursor-crosshair rounded-[var(--radius-sm)] border border-border"
      />
    </div>
  );
}